import { useState, useEffect, useCallback } from 'react';
import { datasetsRecentlyViewedDB, RecentlyViewedDataset } from './datasets-recently-viewed-db';

export function useRecentlyViewed(source_type: 'github' | 'uploaded', limit: number = 5) {
  const [items, setItems] = useState<RecentlyViewedDataset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch items from Dexie
  const fetchItems = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const fetchedItems = await datasetsRecentlyViewedDB.recentlyViewed
        .where('source_type')
        .equals(source_type)
        .reverse()
        .sortBy('timestamp');
      
      setItems(fetchedItems.slice(0, limit));
    } catch (err) {
      console.error('Error fetching recent items:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [source_type, limit]);

  // Initial fetch and setup
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const addRecentlyViewed = useCallback(async (item: Omit<RecentlyViewedDataset, 'timestamp'>) => {
    try {
      const newItem: RecentlyViewedDataset = {
        ...item,
        timestamp: Date.now()
      };

      // Upsert the item (update if exists, insert if not)
      await datasetsRecentlyViewedDB.recentlyViewed.put(newItem);
      
      // Clean up old items - keep only last 20 items per source type
      const allItems = await datasetsRecentlyViewedDB.recentlyViewed
        .where('source_type')
        .equals(item.source_type)
        .reverse()
        .sortBy('timestamp');
      
      if (allItems.length > 20) {
        const itemsToDelete = allItems.slice(20);
        const idsToDelete = itemsToDelete.map((deletableItem: RecentlyViewedDataset) => deletableItem.id);
        await datasetsRecentlyViewedDB.recentlyViewed.bulkDelete(idsToDelete);
      }

      // Refresh the local state
      await fetchItems();
    } catch (error) {
      console.error('Error adding recently viewed item:', error);
    }
  }, [fetchItems]);

  const removeRecentlyViewed = useCallback(async (id: string) => {
    try {
      await datasetsRecentlyViewedDB.recentlyViewed.delete(id);
      // Refresh the local state
      await fetchItems();
    } catch (error) {
      console.error('Error removing recently viewed item:', error);
    }
  }, [fetchItems]);

  return {
    items,
    isLoading,
    error,
    addRecentlyViewed,
    removeRecentlyViewed,
    refresh: fetchItems
  };
} 